import { useContext, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';
import { IGroundTruth, IField, IDocument } from '../../../types/my-types';
import useGetAllFields from '../../hooks/useGetAllFields';

export interface ISpreadsheetRow {
  id: string; // document id
  [fieldId: string]: string;
}

export function useTrainingSetSpreadsheet(docs: IDocument[], trainingSetId: string) {
  const { dataPoolId } = useContext(AppContext);
  const glynt = useGlynt(dataPoolId);

  // Fetch all fields to define columns
  const { data: fieldsData, loading: fieldsLoading } = useGetAllFields(trainingSetId, 1000);

  // Fetch ground truths for each document
  const queries = docs.map((doc) => ({
    queryKey: ['groundTruths', doc.id, trainingSetId, 'spreadsheet'],
    queryFn: async () =>
      (await glynt).groundTruths.list({
        advanced: true,
        document: doc.id,
        training_set: trainingSetId,
        limit: 1000,
      }),
    enabled: !!dataPoolId && !!trainingSetId,
    refetchOnWindowFocus: false,
  }));

  const results = useQueries({
    queries: queries
  });
  const isLoading = fieldsLoading || results.some((r) => r.isLoading);

  // Pivot data
  const { rows, columns } = useMemo(() => {
    if (!fieldsData) {
      return { rows: [], columns: [] };
    }

    const fields: IField[] = fieldsData.pages.flatMap((page) => page.results);

    // Create columns from fields
    const cols = fields.map((field) => ({
      field: field.id,
      headerName: field.label,
      sortable: true,
      resizable: true,
    }));

    // Group GTs by document
    const rowsByDoc: Record<string, ISpreadsheetRow> = {};

    results.forEach((result, index) => {
      const doc = docs[index];
      if (!doc) return;

      if (!rowsByDoc[doc.id]) {
        rowsByDoc[doc.id] = { id: doc.id };
      }

      if (result.data && result.data.results) {
        result.data.results.forEach((gt: IGroundTruth) => {
          // Find the field that matches this ground truth
          // Ground truth has 'field' as a URL, we need to match it to a field ID
          const field = fields.find((f) => f.url === gt.field);
          if (field) {
            rowsByDoc[doc.id][field.id] = gt.content;
          } else {
            // Fallback to extracting ID from URL if field not found in list
            const fieldId = getIdFromUrl(gt.field);
            console.warn(
              `[useTrainingSetSpreadsheet] Field not found in fields list. ` +
                `GT field URL: ${gt.field}, extracted ID: ${fieldId}, document: ${doc.id}. ` +
                `This may indicate incomplete field data or a data consistency issue.`
            );
            rowsByDoc[doc.id][fieldId] = gt.content;
          }
        });
      }
    });

    return {
      rows: Object.values(rowsByDoc),
      columns: cols,
    };
  }, [fieldsData, results, docs]);

  return {
    rows,
    columns,
    isLoading,
  };
}
